import { MemberGroupProjectsSkeleton } from "@components/common/skeleton-loading/member-group-projects-skeleton";
import { MemberGroupProjectsTable } from "@components/table/member-group-projects/member-group-projects-table";
import { MemberGroupProjectsHeaders } from "@components/table/member-group-projects/member-group-projects-table-utils";
import { BulkRemoveFromProjects } from "@components/table/member-group-projects/bulk-actions/bulk-remove-from-projects";
import { BulkChangeGroupProjectRole } from "@components/table/member-group-projects/bulk-actions/bulk-change-group-project-role";
import { MemberGroupProjectsBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { BaseMemberProps } from "@custom-types/member-types";
import { isGroupRole } from "@custom-types/member-type-guards";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMemberGroupDetails } from "@hooks/use-member-group-details";
import { Box } from "@mui/material";
import { companyMemberGroupProjectsSelector } from "@store/company-member-groups/company-member-groups-selector";
import {
  CompanyMemberGroupEntity,
  removeGroup,
} from "@store/company-member-groups/company-member-groups-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { neutral } from "@flat-ui";
import { CommonStyles, DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { useCallback, useEffect, useRef } from "react";

interface Props extends BaseMemberProps {
  /** Group details of a company member */
  group: CompanyMemberGroupEntity;
}

/**
 * Renders a table with projects of a group that a member belongs to
 */
export function MemberGroupProjects({
  group,
  member,
}: Props): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { canRemoveMemberFromProject, canChangeMemberProjectRole } =
    useHasUserValidRoleCompanyLevel();
  const { groupDetails, isFetchingGroupDetails, removeProjects } =
    useMemberGroupDetails({
      groupId: group.id,
    });

  const projects = useAppSelector(companyMemberGroupProjectsSelector(group.id));

  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const pendingRemovedIds = useRef<string[]>([]);

  /**
   * After the success dialog closes, removes the pending projects from Redux
   * (or the entire group if no projects remain and the member isn't a group manager).
   */
  useEffect(() => {
    if (pendingRemovedIds.current.length > 0 && !shouldShowSuccessDialog) {
      const ids = pendingRemovedIds.current;
      pendingRemovedIds.current = [];

      const remainingCount = projects.length - ids.length;

      const isGroupManager = group.role && isGroupRole(group.role);
      if (remainingCount <= 0 && !isGroupManager) {
        // The member was only in the group through project access.
        // Remove the group from Redux so re-expanding won't 404.
        dispatch(removeGroup(group.id));
      } else {
        // Either some projects remain, or the member is a group manager
        // who still belongs to the group regardless of project count.
        removeProjects(ids);
      }
    }
  }, [
    shouldShowSuccessDialog,
    removeProjects,
    projects.length,
    dispatch,
    group.id,
    group.role,
  ]);

  /** Store the removed project IDs to apply after the success dialog closes */
  const handleSuccessfulRemoval = useCallback((removedProjectIds: string[]) => {
    pendingRemovedIds.current = removedProjectIds;
  }, []);

  // Show skeleton only on first load (no cached data yet)
  if (groupDetails?.projectIds === undefined) {
    if (isFetchingGroupDetails) {
      return <MemberGroupProjectsSkeleton />;
    }
    // Fetching failed or hasn't started
    return null;
  }

  if (!projects.length) {
    return (
      <Box
        component={"div"}
        sx={{
          height: "50px",
          display: "flex",
          justifyContent: "center",
          padding: "8px 16px",
          borderBottom: CommonStyles.Borders.gray200Divider,
          my: "30px",
          color: neutral[600],
          fontSize: DEFAULT_TEXT_FONT_SIZE,
        }}
      >
        There are no projects in this group.
      </Box>
    );
  }

  const hasBulkActions =
    canRemoveMemberFromProject || canChangeMemberProjectRole;

  const bulkActionButtons: MemberGroupProjectsBulkButtonComponents | undefined =
    hasBulkActions
      ? {
          removeMemberFromProject: canRemoveMemberFromProject ? (
            <BulkRemoveFromProjects
              member={member}
              projects={projects}
              onSuccessfulComplete={handleSuccessfulRemoval}
            />
          ) : null,
          changeMemberProjectRole: canChangeMemberProjectRole ? (
            <BulkChangeGroupProjectRole member={member} projects={projects} />
          ) : null,
        }
      : undefined;

  return (
    <MemberGroupProjectsTable
      projects={projects}
      isLoading={isFetchingGroupDetails}
      requiredColumns={[
        MemberGroupProjectsHeaders.thumbnail,
        MemberGroupProjectsHeaders.name,
        MemberGroupProjectsHeaders.role,
        MemberGroupProjectsHeaders.options,
      ]}
      bulkActionButtons={bulkActionButtons}
      shouldHideCheckbox={!hasBulkActions}
    />
  );
}
